import { useCallback, useMemo } from 'react';

import {
  canInviteMembers,
  isMemberTypeOfBoard,
} from '@trello/business-logic/board';
import { isMemberOfOrganization } from '@trello/business-logic/organization';
import { memberId as loggedInMemberId } from '@trello/session-cookie';

import type { LoggedInMemberPermissionsQuery } from './LoggedInMemberPermissionsQuery.generated';
import { useLoggedInMemberPermissionsQuery } from './LoggedInMemberPermissionsQuery.generated';

interface LoggedInMemberPerms {
  canInviteBoardMembers: boolean;
  isOrgMember: boolean;
  isObserver: boolean;
}

interface LoggedInMemberPermissionsResult {
  getMemberPermissions?: (
    loggedInMemberData: LoggedInMember,
  ) => LoggedInMemberPerms;
  isLoading: boolean;
  loggedInMember: LoggedInMember;
  memberPermissions: LoggedInMemberPerms;
}

export type LoggedInMember = LoggedInMemberPermissionsQuery['member'];

export const useLoggedInMemberPermissions = ({
  boardId,
}: {
  boardId: string;
}): LoggedInMemberPermissionsResult => {
  // If we have no session logged in member make sure we skip querying
  const { data, loading } = useLoggedInMemberPermissionsQuery(
    loggedInMemberId
      ? {
          variables: { memberId: loggedInMemberId, boardId },
          waitOn: ['CurrentBoardFull', 'MemberHeader'],
          fetchPolicy: 'cache-only',
        }
      : { skip: true, waitOn: ['None'] },
  );

  const loggedInMemberData = data?.member;
  const board = data?.board;
  const organization = data?.board?.organization;

  const getMemberPermissions = useCallback(
    (loggedInMemberData: LoggedInMember) => {
      const loggedInMemberIsOrgMember =
        loggedInMemberData && organization
          ? isMemberOfOrganization(loggedInMemberData, organization)
          : false;

      const loggedInMemberCanInviteBoardMembers =
        loggedInMemberData && board
          ? canInviteMembers(loggedInMemberData, board)
          : false;

      const isObserver =
        loggedInMemberData && board
          ? isMemberTypeOfBoard(loggedInMemberData, board, 'observer', true)
          : false;

      return {
        canInviteBoardMembers: loggedInMemberCanInviteBoardMembers,
        isOrgMember: loggedInMemberIsOrgMember,
        isObserver,
      };
    },
    [organization, board],
  );

  const memberPermissionData = useMemo(
    () => ({
      isLoading: loading,
      memberPermissions: getMemberPermissions(loggedInMemberData),
    }),
    [loading, loggedInMemberData, getMemberPermissions],
  );

  if (!loggedInMemberData) {
    return {
      isLoading: false,
      loggedInMember: null,
      memberPermissions: {
        canInviteBoardMembers: false,
        isOrgMember: false,
        isObserver: false,
      },
    };
  }

  return {
    ...memberPermissionData,
    getMemberPermissions,
    loggedInMember: loggedInMemberData,
  };
};
